import { getIntentLink, getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { VisibleIcon } from "@components/icon";
import { formatHoursToTimeFormat, longDateFormat } from "@components/inputs/date/utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { SwitchType } from "@components/inputs/switch/Switch";
import { ifAll, IFieldDefFn, IGetValueArgs, TGetValueFn } from "@components/smart/FieldInfo";
import { getCommonFilterDefs, withDisplayName } from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem, ISummaryItemEditProps } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { isRowValueCellValueObject } from "@components/table/TableUtils";
import {
    EntitySetName,
    EntityTypeName,
    IPrAttendanceDayEntity,
    IPrAttendanceDayIntervalEntity,
    IPrAttendanceEntity,
    IPrAttendanceStatusDisplayEntity,
    IPrEmploymentEntity,
    PrAbsenceCategoryEntity,
    PrAbsenceCategoryTemporalCurrentEntity,
    PrAbsenceCategoryTypeEntity,
    PrAttendanceDayEntity,
    PrAttendanceDayIntervalEntity,
    PrAttendanceEntity,
    PrAttendanceStatusDisplayEntity,
    PrEmployeeEntity,
    PrEmploymentBenefitEntity,
    PrEmploymentEntity,
    PrEmploymentTemplateEntity,
    PrEmploymentTemplateTemporalEntity,
    PrEmploymentTemporalEntity,
    PrEmploymentTypeEntity,
    PrSocialEventEntity,
    PrWorkingPatternDayEntity,
    PrWorkingPatternEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrAbsenceCategoryTypeCode,
    PrAttendanceStatusCode,
    PrAttendanceStatusDisplayCode,
    PrEmploymentTypeCode,
    PrWorkIntervalTypeCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import { AttendanceCalendarWrapper } from "@pages/payroll/attendance/Attendance.styles";
import { AttendanceViewMode } from "@pages/payroll/attendance/Attendance.types";
import {
    employeeNameFormatter,
    getEmployeeFilterDef,
    getEmployeeSummaryItemDef,
    getEmployeeTableColumnDefs
} from "@pages/payroll/employee/Employee.utils";
import { getHoursDiffFormatted } from "@pages/payroll/workingPatterns/WorkingPatterns.utils";
import { isDefined } from "@utils/general";
import i18next from "i18next";
import React from "react";
import { DefaultTheme } from "styled-components";

import Calendar from "../../../components/calendar";
import { DASH_CHARACTER } from "../../../constants";
import {
    BasicInputSizes,
    CacheStrategy,
    FieldType,
    LabelStatus,
    NavigationSource,
    Sort,
    TextAlign
} from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BindingContext, { createPath } from "../../../odata/BindingContext";
import { ROUTE_EMPLOYMENT } from "../../../routes";
import DateType, { getDateFormat, getUtcDayjs } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import {
    balanceFormatter,
    getTopPriorityDayAbsence,
    getWorkingPattern,
    isAbsenceDisabled,
    isAbsenceTimeDisabled,
    isDayIntervalTimeVisible,
    renderLinkToSocialEvent,
    SocialEventCategoryTypes,
    timeStampToMinutesFormatter,
    timeStampToMinutesParser
} from "./Attendance.utils";
import AttendanceDayEditEntries from "./AttendanceDayEditEntries";
import AttendanceDaysList from "./AttendanceDaysList";
import AttendanceFormView, { IAttendanceCustomData } from "./AttendanceFormView";
import MonthlyOverview from "./MonthlyOverview";
import UsedWorkingPatternOverview from "./UsedWorkingPatternOverview";

function getThemeColorForPaymentStatus(statusCode: PrAttendanceStatusDisplayCode): keyof DefaultTheme {
    switch (statusCode) {
        case PrAttendanceStatusDisplayCode.ToProcess:
        case PrAttendanceStatusDisplayCode.NoAttendance:
            return "C_SEM_text_bad";
        default:
            return "C_SEM_text_good";
    }
}

export const viewSwitchPath = BindingContext.localContext("ViewSwitch");
export const calendarPath = BindingContext.localContext("Calendar");
export const tablePath = BindingContext.localContext("Table");
export const WorkedHoursPath = BindingContext.localContext("WorkedHours");
export const SaldoPath = BindingContext.localContext("Saldo");
export const DayWorkStartPath = BindingContext.localContext("DayWorkStart");
export const IsFullDayAbsencePath = BindingContext.localContext("IsFullDayAbsence");
export const HasAbsencePath = BindingContext.localContext("HasAbsence");
export const timeDiffPath = BindingContext.localContext("TimeDiff");
export const entriesBackupPath = BindingContext.localContext("EntriesBackup");

export const SocialEventLinkPath = BindingContext.localContext("SocialEventLink");
export const PlaceholderEventLinkPath = BindingContext.localContext("PlaceholderEventLink");

const handleDaySelect = (selectedDays: Set<number>, focusedDay: number, storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): void => {
    storage.setCustomData({ selectedDays, focusedDay });
    storage.refresh();
};

const isNotStatutoryCompensationType: TGetValueFn<boolean> = (args) => {
    const attendance = args?.storage.data.entity as IPrAttendanceEntity;
    return !attendance?.IsStatutoryCompensation;
};

const hasWorkInterval: TGetValueFn<boolean> = args => {
    const attendanceDay = args.storage.getValue(args.bindingContext.getParent()) as IPrAttendanceDayEntity;
    return attendanceDay.Intervals.some(i => !i.AbsenceCategory?.Id);
};

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrAttendance}Table`,
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Employee),
                createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.NumberOurs),
                createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Type),
                PrAttendanceEntity.StatusDisplay,
                PrAttendanceEntity.Balance
            ],
            filterDefinition: {
                ...getEmployeeFilterDef(createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Employee)),
                [createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.NumberOurs)]: {},
                [createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Type)]: {
                    label: i18next.t("Attendance:Table.Type"),
                    fieldSettings: {
                        displayName: PrEmploymentTypeEntity.Name
                    }
                },
                ...withDisplayName(PrAttendanceEntity.StatusDisplay),
                [PrAttendanceEntity.Balance]: {},
                ...getCommonFilterDefs()
            },
            isValueHelp: true
        }],
        initialSortBy: [{
            id: createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Employee),
            sort: Sort.Desc
        }],
        columns: [
            createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Employee, PrEmployeeEntity.NumberOurs),
            createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Employee),
            createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.NumberOurs),
            createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name),
            PrAttendanceEntity.StatusDisplay,
            PrAttendanceEntity.Balance
        ],
        columnDefinition: {
            ...getEmployeeTableColumnDefs(createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Employee)),
            [createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.NumberOurs)]: {
                label: i18next.t("Attendance:Table.Employment"),
                formatter: (val, { storage, entity }: IFormatOptions<IPrAttendanceEntity>) => {
                    const { Id } = entity.Employment;
                    return getTableIntentLink(val as string, {
                        route: `${ROUTE_EMPLOYMENT}/${Id}`,
                        context: storage.context,
                        storage
                    });
                }
            },
            [PrAttendanceEntity.StatusDisplay]: {
                label: i18next.t("Attendance:Table.Status"),
                formatter: (val, args) => {
                    const entity = args.entity as IPrAttendanceEntity;
                    return {
                        value: <ColoredText
                            color={getThemeColorForPaymentStatus(entity.StatusDisplay?.Code as PrAttendanceStatusDisplayCode)}>
                            {entity.StatusDisplay?.Name}
                        </ColoredText>,
                        tooltip: entity.StatusDisplay?.Name
                    };
                }
            },
            [PrAttendanceEntity.Balance]: {
                label: i18next.t("Attendance:Table.Balance"),
                formatter: balanceFormatter
            },
            [createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name)]: {
                label: i18next.t("Attendance:Table.Type"),
            }
        },
        additionalProperties: [
            { id: PrAttendanceEntity.IsStatutoryCompensation }
        ],
        title: i18next.t("Attendance:Title"),
    };

    const summary: ISummaryItem[] = [
        getEmployeeSummaryItemDef(createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Employee)), {
            id: PrAttendanceEntity.Employment,
            label: i18next.t("Attendance:Summary.Employment"),
            formatter: (_: TValue, { storage }: IFormatOptions) => {
                const employment = storage.getValueByPath(PrAttendanceEntity.Employment) as IPrEmploymentEntity;
                return getIntentLink(employment.NumberOurs, {
                    route: `${ROUTE_EMPLOYMENT}/${employment.Id}`,
                    context: storage.context,
                    storage
                });
            },
            additionalProperties: [
                { id: PrEmploymentEntity.NumberOurs }
            ]
        }, {
            id: PrAttendanceEntity.Balance,
            label: i18next.t("Attendance:Summary.Balance"),
            isVisible: isNotStatutoryCompensationType,
            formatter: (val, args) => {
                const entity = args.entity as IPrAttendanceEntity;
                const formattedBalance = balanceFormatter(entity.Balance, args);
                return isRowValueCellValueObject(formattedBalance) ? formattedBalance?.value : formattedBalance;
            }
        }, {
            id: BindingContext.localContext("WorkingPattern"),
            label: i18next.t("Attendance:Summary.WorkingPattern"),
            isVisible: isNotStatutoryCompensationType,
            textAlign: TextAlign.Left,
            formatter: (val, args) => {
                const workingPattern = getWorkingPattern(args.entity as IPrAttendanceEntity);
                return workingPattern?.Name ?? DASH_CHARACTER;
            },
            renderEdit: (args: ISummaryItemEditProps) => {
                const workingPattern = getWorkingPattern(args.storage.data.entity as IPrAttendanceEntity);
                if (!isDefined(workingPattern)) {
                    return null;
                }
                return <UsedWorkingPatternOverview
                    storage={args.storage as FormStorage<IPrAttendanceEntity, IAttendanceCustomData>}
                    onClose={args.onClose}
                    workingPattern={workingPattern}
                />;
            },
            iconInfo: {
                icon: VisibleIcon
            }
        }, {
            id: createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name),
            label: i18next.t("Attendance:Table.Type"),
            isVisible: (args) => {
                const attendance = args?.storage.data.entity as IPrAttendanceEntity;
                return attendance?.IsStatutoryCompensation;
            }
        }, {
            id: PrAttendanceEntity.StatusDisplay,
            additionalData: [{ id: PrAttendanceStatusDisplayEntity.Name }],
            label: i18next.t("Attendance:Summary.Status"),
            formatter: (val) => {
                return (val as IPrAttendanceStatusDisplayEntity)?.Name;
            },
            colorFormatter: (val) => {
                return getThemeColorForPaymentStatus((val as IPrAttendanceStatusDisplayEntity).Code as PrAttendanceStatusDisplayCode);
            }
        }];

    const workingPatternAdditionalProperties = [
        { id: PrWorkingPatternEntity.Type },
        { id: PrWorkingPatternEntity.TypeCode },
        { id: PrWorkingPatternEntity.Name },
        { id: PrWorkingPatternEntity.DefaultDayStart },
        { id: PrWorkingPatternEntity.DateBalancingPeriodStart },
        { id: PrWorkingPatternEntity.BalancingPeriodWeeks },
        { id: PrWorkingPatternEntity.IsDifferentOddAndEvenWeek },
        { id: PrWorkingPatternEntity.BalancingPeriodWeeks },
        { id: PrWorkingPatternEntity.HoursPerWeekFullTime },
        { id: PrWorkingPatternEntity.WorkingMinutesWeekly },
        {
            id: PrWorkingPatternEntity.Days,
            additionalProperties: [
                { id: PrWorkingPatternDayEntity.WorkingHours },
                { id: PrWorkingPatternDayEntity.Date }
            ]
        }
    ];

    const form: IFormDef = {
        id: `${EntityTypeName.PrAttendance}Form`,
        translationFiles: getDefinitions.translationFiles,
        getItemBreadCrumbText: (storage: Model) => {
            const employee = (storage.data.entity as IPrAttendanceEntity).Employment.Employee;
            return getItemBreadCrumbsText(storage, null, employeeNameFormatter(null, { item: employee }));
        },
        formControl: AttendanceFormView,
        ignoreVariants: true,
        summary,
        isReadOnly: args => {
            return args.storage.getValueByPath(PrAttendanceEntity.StatusCode) === PrAttendanceStatusCode.Processed;
        },
        additionalProperties: [{
            id: PrAttendanceEntity.IsStatutoryCompensation
        }, {
            id: PrAttendanceEntity.Status
        }, {
            id: PrAttendanceEntity.Month
        }, {
            id: PrAttendanceEntity.Year
        }, {
            id: PrAttendanceEntity.Employment,
            additionalProperties: [{
                id: PrEmploymentEntity.TypeCode,
            }, {
                id: PrEmploymentEntity.DateStart
            }, {
                id: PrEmploymentEntity.DateEnd
            }, {
                id: PrEmploymentEntity.NumberOurs
            }, {
                id: PrEmploymentEntity.Template,
                additionalProperties: [{
                    id: PrEmploymentTemplateEntity.TemporalPropertyBag,
                    additionalProperties: [{
                        id: PrEmploymentTemplateTemporalEntity.DateValidFrom
                    }, {
                        id: PrEmploymentTemplateTemporalEntity.DateValidTo
                    }, {
                        id: PrEmploymentTemplateTemporalEntity.WorkingPattern,
                        additionalProperties: workingPatternAdditionalProperties
                    }]
                }]
            }, {
                id: PrEmploymentEntity.TemporalPropertyBag,
                additionalProperties: [{
                    id: PrEmploymentTemporalEntity.DateValidFrom
                }, {
                    id: PrEmploymentTemporalEntity.DateValidTo
                }, {
                    id: PrEmploymentTemporalEntity.WorkingPattern,
                    additionalProperties: workingPatternAdditionalProperties
                }]
            }, {
                id: PrEmploymentEntity.Benefits,
                additionalProperties: [{
                    id: PrEmploymentBenefitEntity.TypeCode
                }, {
                    id: PrEmploymentBenefitEntity.DateValidFrom
                }, {
                    id: PrEmploymentBenefitEntity.DateValidTo
                }]
            }]
        }, {
            id: PrAttendanceEntity.Days,
            additionalProperties: [
                { id: PrAttendanceDayEntity.Planned },
                { id: WorkedHoursPath }, // local context, here just for info load
                { id: SaldoPath }, // local context, here just for info load
                { id: HasAbsencePath }, // local context, here just for info load
                { id: IsFullDayAbsencePath }, // local context, here just for info load
                { id: SocialEventLinkPath }, // local context, here just for info load
                { id: PlaceholderEventLinkPath }, // local context, here just for info load
                { id: DayWorkStartPath }, // local context, here just for info load
                { id: PrAttendanceDayEntity.Date },
                { id: PrAttendanceDayEntity.IsExcludedDay },
                { id: PrAttendanceDayEntity.IsOnRegistry },
                { id: PrAttendanceDayEntity.IsExcludedPeriod },
                {
                    id: PrAttendanceDayEntity.Intervals,
                    additionalProperties: [
                        {
                            id: PrAttendanceDayIntervalEntity.SocialEvent,
                            additionalProperties: [{
                                id: PrSocialEventEntity.NumberOurs
                            }, {
                                id: PrSocialEventEntity.SocialEventTypeCode
                            }]
                        },
                        {
                            id: PrAttendanceDayIntervalEntity.AbsenceCategory,
                            additionalProperties: [{
                                id: PrAbsenceCategoryEntity.TypeCode
                            }, {
                                id: PrAbsenceCategoryEntity.Color
                            }, {
                                id: PrAbsenceCategoryEntity.CurrentTemporalPropertyBag,
                                additionalProperties: [{
                                    id: PrAbsenceCategoryTemporalCurrentEntity.Name
                                }]
                            }, {
                                id: PrAbsenceCategoryEntity.Type,
                                additionalProperties: [{ id: PrAbsenceCategoryTypeEntity.Priority }]
                            }]
                        },
                        { id: PrAttendanceDayIntervalEntity.TimeEnd },
                        { id: PrAttendanceDayIntervalEntity.TimeStart },
                        { id: PrAttendanceDayIntervalEntity.WorkType },
                        { id: PrAttendanceDayIntervalEntity.Order },
                        { id: PrAttendanceDayIntervalEntity.IsWholeDay },
                        { id: PrAttendanceDayIntervalEntity.IsStartNextDay },
                        { id: PrAttendanceDayIntervalEntity.IsEndNextDay },
                        { id: PrAttendanceDayIntervalEntity.IsPlaceholder }
                    ]
            }]
        }],
        fieldDefinition: {
            [viewSwitchPath]: {
                type: FieldType.SegmentedButton,
                defaultValue: AttendanceViewMode.Calendar,
                labelStatus: LabelStatus.Removed,
                isVisible: isNotStatutoryCompensationType,
                fieldSettings: {
                    items: [{
                        id: AttendanceViewMode.Calendar,
                        iconName: "Date",
                        title: i18next.t("Attendance:Form.Calendar")
                    }, {
                        id: AttendanceViewMode.Table,
                        iconName: "List",
                        title: i18next.t("Attendance:Form.Table")
                    }],
                    overrideFieldProps: {
                        isDisabled: false,
                        isReadOnly: false
                    }
                },
            },
            [tablePath]: {
                type: FieldType.Custom,
                isVisible: ifAll(isNotStatutoryCompensationType, (args: IGetValueArgs) => {
                    return args.storage.getValueByPath(viewSwitchPath) === AttendanceViewMode.Table;
                }),
                render: (args: IFieldDefFn) => {
                    const storage = args.storage as FormStorage<IPrAttendanceEntity, IAttendanceCustomData>;
                    const saldoMap = storage.getCustomData()?.saldoMap ?? {};

                    return <AttendanceDaysList
                        saldoMap={saldoMap}
                        storage={storage}/>;
                },
                customizationData: {
                    useForCustomization: false
                }
            },
            [calendarPath]: {
                type: FieldType.Custom,
                isVisible: (args: IGetValueArgs) => {
                    return args.storage.getValueByPath(viewSwitchPath) !== AttendanceViewMode.Table;
                },
                render: (args: IFieldDefFn) => {
                    const storage = args.storage as FormStorage<IPrAttendanceEntity, IAttendanceCustomData>;
                    const entity = storage.data.entity;

                    if (entity.IsStatutoryCompensation) {
                        // we render just text with info that there is no calendar for statutory compensation type
                        return (
                            <div>{i18next.t("Attendance:Form.NoAttendance")}</div>
                        );
                    }

                    const {
                        actions,
                        holidays,
                        workedHoursMap
                    } = storage.getCustomData();
                    const month = getUtcDayjs().set("month", entity.Month - 1).set("year", entity.Year);
                    const selectedDayIndex = storage.getCustomData().focusedDay;
                    const employment = storage.getValueByPath(PrAttendanceEntity.Employment) as IPrEmploymentEntity;
                    const firstAvailableDay = employment.DateStart && month.isSame(employment.DateStart, "month") ? getUtcDayjs(employment.DateStart).date() : undefined;
                    const lastAvailableDay = employment.DateEnd && month.isSame(employment.DateEnd, "month") ? getUtcDayjs(employment.DateEnd).date() : undefined;
                    return <AttendanceCalendarWrapper>
                        <Calendar
                            month={month}
                            workedHoursMap={workedHoursMap}
                            actions={actions}
                            holidays={holidays}
                            firstAvailableDay={firstAvailableDay}
                            lastAvailableDay={lastAvailableDay}
                            onDaySelect={(days, focusedDay) => handleDaySelect(days, focusedDay, storage)}
                            selectedDays={storage.getCustomData().selectedDays}
                            focusedDay={selectedDayIndex}
                        />
                        {selectedDayIndex
                            ? <AttendanceDayEditEntries
                                onChange={args.events.onChange}
                                storage={storage}
                                selectedDay={selectedDayIndex}/>
                            : <MonthlyOverview storage={storage} />
                        }
                    </AttendanceCalendarWrapper>;
                },
                customizationData: {
                    useForCustomization: false
                }
            },
            [createPath(PrAttendanceEntity.Days, PrAttendanceDayEntity.Intervals, PrAttendanceDayIntervalEntity.AbsenceCategory)]: {
                type: FieldType.ComboBox,
                cacheStrategy: CacheStrategy.Route,
                isDisabled: isAbsenceDisabled,
                width: BasicInputSizes.L,
                filter: {
                    select: `${PrAbsenceCategoryEntity.IsActive} eq true`
                },
                fieldSettings: {
                    displayName: "CurrentTemporalPropertyBag/Name",
                    preloadItems: true,
                    // groups: [{
                    //     id: GENERAL_ABSENCE_CATEGORY_GROUP_ID,
                    //     title: i18next.t("Attendance:Form.GeneralCategory")
                    // }, {
                    //     id: SOCIAL_EVENT_CATEGORY_GROUP_ID,
                    //     title: i18next.t("Attendance:Form.SocialEventCategory")
                    // }],
                    additionalProperties: [
                        { id: PrAbsenceCategoryEntity.Color },
                        { id: PrAbsenceCategoryEntity.IsActive },
                        { id: PrAbsenceCategoryEntity.IsExcludedDay },
                        { id: PrAbsenceCategoryEntity.IsExcludedPeriod },
                        { id: PrAbsenceCategoryEntity.IsOnRegistry },
                        {
                            id: PrAbsenceCategoryEntity.Type,
                            additionalProperties: [{
                                id: PrAbsenceCategoryTypeEntity.Origin
                            }, {
                                id: PrAbsenceCategoryTypeEntity.Priority
                            }]
                        },
                    ],
                    itemsForRender: (items: ISelectItem[], args: IGetValueArgs) => {
                        // enhance with groupId for grouping in combobox
                        // items.forEach(i => {
                        //     const type = i.additionalData.Type?.Code as PrAbsenceCategoryTypeCode;
                        //     if (SocialEventCategoryTypes.includes(type)) {
                        //         i.groupId = SOCIAL_EVENT_CATEGORY_GROUP_ID;
                        //     } else {
                        //         i.groupId = GENERAL_ABSENCE_CATEGORY_GROUP_ID;
                        //     }
                        // });

                        // sort by groupId
                        // items.sort((a: ISelectItem, b: ISelectItem) => {
                        //     return sortCompareFn(a.groupId, b.groupId);
                        // });

                        // DEV-45310 - https://solitea-cz.atlassian.net/browse/DEV-45310
                        items = items.filter(i => {
                            const type = i.additionalData.Type?.Code as PrAbsenceCategoryTypeCode;
                            return !SocialEventCategoryTypes.includes(type);
                        });

                        const attendance = args.storage.data.entity as IPrAttendanceEntity;
                        if (attendance.Employment.TypeCode === PrEmploymentTypeCode.WorkAgreementDPP) {
                            return items.filter(i => i.additionalData.SystemTypeCode !== PrAbsenceCategoryTypeCode.SickDay);
                        }
                        return items;
                    },
                    localDependentFields: [{
                        from: { id: PrAbsenceCategoryEntity.Color },
                        to: { id: PrAbsenceCategoryEntity.Color },
                        navigateFrom: NavigationSource.Itself
                    }, {
                        from: { id: PrAbsenceCategoryEntity.TypeCode },
                        to: { id: PrAbsenceCategoryEntity.TypeCode },
                        navigateFrom: NavigationSource.Itself
                    }]
                }
            },
            [createPath(PrAttendanceEntity.Days, PrAttendanceDayEntity.Intervals, PrAttendanceDayIntervalEntity.WorkType)]: {
                type: FieldType.ComboBox,
                cacheStrategy: CacheStrategy.EndOfTime,
                fieldSettings: {
                    displayName: "Name",
                    itemsForRender: (origItems: ISelectItem[], args: IGetValueArgs) => {
                        const { holidays, focusedDay } = args.storage.getCustomData() as IAttendanceCustomData;
                        // if there is no holiday on the focused day or the next day, we will allow the CompensationForHoliday option on the list
                        if (!holidays.some(h => {
                            const dayIndex = getUtcDayjs(h).date();
                            return dayIndex === focusedDay || dayIndex === focusedDay + 1;
                        })) {
                            return origItems.filter(i => i.id !== PrWorkIntervalTypeCode.CompensationForHoliday);
                        }
                        return origItems;
                    }
                },
                isDisabled: isAbsenceDisabled,
                formatter: (code, args) => {
                    const entity = args.storage.getValue(args.bindingContext.getParent());
                    return entity?.AbsenceCategory?.CurrentTemporalPropertyBag?.Name ?? getEnumDisplayValue(EntityTypeName.PrWorkIntervalType, code as PrWorkIntervalTypeCode) ?? DASH_CHARACTER;
                },
                defaultValue: (args: IGetValueArgs) => {
                    const dayBc = args.bindingContext.getParentCollection().getParent();
                    const day = args.storage.getValue(dayBc) as IPrAttendanceDayEntity;
                    const lastInterval = day.Intervals?.at(-1);
                    return lastInterval?.WorkType?.Code === PrWorkIntervalTypeCode.Work ? PrWorkIntervalTypeCode.MealBreak : PrWorkIntervalTypeCode.Work;
                }
            },
            [createPath(PrAttendanceEntity.Days, PrAttendanceDayEntity.Intervals, PrAttendanceDayIntervalEntity.TimeStart)]: {
                type: FieldType.Time,
                width: BasicInputSizes.XS,
                isDisabled: isAbsenceTimeDisabled,
                isVisible: isDayIntervalTimeVisible,
                textAlign: TextAlign.Right,
                fieldSettings: {
                    showSteppers: false
                }
            },
            [createPath(PrAttendanceEntity.Days, PrAttendanceDayEntity.Intervals, PrAttendanceDayIntervalEntity.TimeEnd)]: {
                type: FieldType.Time,
                width: BasicInputSizes.XS,
                isDisabled: isAbsenceTimeDisabled,
                isVisible: isDayIntervalTimeVisible,
                textAlign: TextAlign.Right,
                fieldSettings: {
                    showSteppers: false
                }
            },
            [createPath(PrAttendanceEntity.Days, PrAttendanceDayEntity.Intervals, timeDiffPath)]: {
                isReadOnly: true,
                width: BasicInputSizes.XS,
                label: i18next.t("Attendance:Form.Length"),
                fieldSettings: {
                    unit: i18next.t("Components:Calendar.HourPlaceholder")
                },
                textAlign: TextAlign.Right,
                formatter: (val, args) => {
                    const interval = args.storage.getValue(args.bindingContext.getParent()) as IPrAttendanceDayIntervalEntity;
                    return getHoursDiffFormatted(interval?.TimeStart, interval?.TimeEnd, true);
                }
            },
            [createPath(PrAttendanceEntity.Days, PrAttendanceDayEntity.Planned)]: {
                type: FieldType.Time,
                width: BasicInputSizes.S,
                parser: timeStampToMinutesParser,
                defaultValue: 0,
                formatter: timeStampToMinutesFormatter,
                fieldSettings: {
                    unit: i18next.t("Components:Calendar.HourPlaceholder")
                }
            },
            [createPath(PrAttendanceEntity.Days, PrAttendanceDayEntity.IsOnRegistry)]: {
                type: FieldType.Checkbox,
                defaultValue: true,
                label: i18next.t("Attendance:Form.IsOnRegistry"),
                labelStatus: LabelStatus.Removed
            },
            [createPath(PrAttendanceEntity.Days, PrAttendanceDayEntity.IsExcludedDay)]: {
                type: FieldType.Checkbox,
                label: i18next.t("Attendance:Form.IsExcludedDay"),
                labelStatus: LabelStatus.Removed,
                isDisabled: hasWorkInterval
            },
            [createPath(PrAttendanceEntity.Days, PrAttendanceDayEntity.IsExcludedPeriod)]: {
                type: FieldType.Checkbox,
                label: i18next.t("Attendance:Form.IsExcludedPeriod"),
                labelStatus: LabelStatus.Removed,
                isDisabled: hasWorkInterval
            },
            [createPath(PrAttendanceEntity.Days, WorkedHoursPath)]: {
                type: FieldType.Time,
                width: BasicInputSizes.S,
                label: i18next.t("Attendance:DaysList.Worked"),
                textAlign: TextAlign.Right,
                isReadOnly: (args: IGetValueArgs): boolean => {
                    const dayBc = args.bindingContext.getParent();
                    return args.storage.getValue(dayBc.navigate(IsFullDayAbsencePath)) as boolean;
                },
                formatter: (val, args) => {
                    const dayBc = args.bindingContext.getParent();
                    const hasFullDayAbsence = args.storage.getValue(dayBc.navigate(IsFullDayAbsencePath)) as boolean;
                    if (hasFullDayAbsence) {
                        return formatHoursToTimeFormat(0);
                    }
                    return DateType.format(val as Date, longDateFormat(getDateFormat(DateType.defaultTimeFormat)));
                },
                fieldSettings: {
                    unit: i18next.t("Components:Calendar.HourPlaceholder")
                }
            },
            [createPath(PrAttendanceEntity.Days, DayWorkStartPath)]: {
                type: FieldType.Time,
                width: BasicInputSizes.S,
                label: i18next.t("Attendance:DaysList.Start"),
                textAlign: TextAlign.Right,
                isReadOnly: (args: IGetValueArgs): boolean => {
                    const dayBc = args.bindingContext.getParent();
                    return args.storage.getValue(dayBc.navigate(IsFullDayAbsencePath)) as boolean;
                },
                formatter: (val, args) => {
                    return DateType.format(val as Date, longDateFormat(getDateFormat(DateType.defaultTimeFormat)));
                },
            },
            [createPath(PrAttendanceEntity.Days, SocialEventLinkPath)]: {
                type: FieldType.Custom,
                label: i18next.t("Attendance:DaysList.SocialEvent"),
                isReadOnly: true,
                isVisible: (args: IGetValueArgs): boolean => {
                    const dayEntity = args.storage.getValue(args.bindingContext.getParent());
                    return !!dayEntity.Intervals.some((i: IPrAttendanceDayIntervalEntity) => !!i.SocialEvent?.Id && !i.IsPlaceholder);
                },
                render: (args: IFieldDefFn) => {
                    const dayEntity = args.storage.getValue(args.bindingContext.getParent());
                    let absenceInterval = getTopPriorityDayAbsence(dayEntity);
                    if (!absenceInterval) {
                        // if there is no whole day absence, we will show the first interval with social event that is not placeholder
                        absenceInterval = dayEntity.Intervals.find(((i: IPrAttendanceDayIntervalEntity) => i.SocialEvent?.Id && !i.IsPlaceholder));
                    }
                    return renderLinkToSocialEvent(absenceInterval, args);
                },
            },
            [createPath(PrAttendanceEntity.Days, PlaceholderEventLinkPath)]: {
                label: i18next.t("Attendance:DaysList.UnconfirmedSocialEvent"),
                isReadOnly: true,
                isVisible: (args: IGetValueArgs): boolean => {
                    const dayEntity = args.storage.getValue(args.bindingContext.getParent());
                    return !!dayEntity.Intervals.some((i: IPrAttendanceDayIntervalEntity) => !!i.SocialEvent?.Id && i.IsPlaceholder);
                },
                render: (args: IFieldDefFn) => {
                    const dayEntity = args.storage.getValue(args.bindingContext.getParent());
                    const absenceInterval = getTopPriorityDayAbsence(dayEntity, true);
                    // placeholders are always whole day, so we don't need to check for other intervals
                    return renderLinkToSocialEvent(absenceInterval, args);
                },
            },
            [createPath(PrAttendanceEntity.Days, SaldoPath)]: {
                label: i18next.t("Attendance:DaysList.Saldo"),
                isReadOnly: true,
                textAlign: TextAlign.Right,
                formatter: (val, args) => {
                    const storage = args.storage as FormStorage<IPrAttendanceEntity, IAttendanceCustomData>;
                    const dayEntity = storage.getValue(args.bindingContext.getParent());
                    const date = dayEntity[PrAttendanceDayEntity.Date];
                    const dayIndex = getUtcDayjs(date).get("date");
                    const saldoMinutes = (storage.getCustomData().saldoMap[dayIndex] ?? 0);
                    return balanceFormatter(saldoMinutes, args);
                },
            },
            [createPath(PrAttendanceEntity.Days, HasAbsencePath)]: {
                type: FieldType.Switch,
                label: i18next.t("Attendance:Form.Absence"),
                isDisabled: (args) => {
                    const day = args.storage.getValue(args.bindingContext.getParent()) as IPrAttendanceDayEntity;
                    return day.Intervals.some(i => !!i.SocialEvent?.Id && !i.IsPlaceholder);
                },
                fieldSettings: {
                    type: SwitchType.YesNo
                },
                affectedFields: [{ id: createPath(PrAttendanceEntity.Days, WorkedHoursPath) }]
            },
            [createPath(PrAttendanceEntity.Days, IsFullDayAbsencePath)]: {
                type: FieldType.SegmentedButton,
                labelStatus: LabelStatus.Removed,
                defaultValue: true,
                isVisible: (args) => {
                    return !!args.storage.getValue(args.bindingContext.getParent().navigate(HasAbsencePath));
                },
                isDisabled: (args) => {
                    const day = args.storage.getValue(args.bindingContext.getParent()) as IPrAttendanceDayEntity;
                    // has any social event as it doesn't make sense to have whole day absence and social event on the same day
                    return day.Intervals.some(i => !!i.SocialEvent?.Id && !i.IsPlaceholder);
                },
                fieldSettings: {
                    items: [{
                        id: false,
                        label: i18next.t("Attendance:AbsenceType.Partial")
                    }, {
                        id: true,
                        label: i18next.t("Attendance:AbsenceType.WholeDay")
                    }]
                }
            }
        },
        groups: [
            {
                id: "main",
                rows: [
                    [
                        { id: viewSwitchPath }
                    ],
                    [
                        { id: calendarPath },
                        { id: tablePath }
                    ]
                ]
            }
        ]
    };
    return {
        entitySet: EntitySetName.PrAttendances,
        table,
        form
    };
};
getDefinitions.translationFiles = ["Attendance", "Common", "Components"];
setDefByEntityType(EntityTypeName.PrAttendance, getDefinitions);

